import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/primitives';
import { Score } from '@/components/Score';
import {
  CoordinateMarks,
  EyebrowLabel,
  LedgerRow,
  SplitPane,
  TickRule,
} from '@/components/identity';
import { ErrorState, LoadingState } from '@/components/DataState';
import { engineByKey } from '@/engines/registry';
import { fetchExecutiveOverview } from '@/data/sources';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { AuraDashboardLauncher } from '@/voice';
import { AuraMicOnboardingCard } from '@/voice/wake/AuraMicOnboardingCard';
import { cn } from '@/lib/utils';
import { InsightText } from '@/components/InsightText';
import type { AttentionItem, VitalSign } from '@/types';
import type { SeoExecSnapshot, SeoMovementState, SeoAlertsState, SeoOrganicState } from '@/types/seod';

/* ----------------------------------------------------------------------------
 * The Executive OS (signature moment, v1.1 §6.2 / §7.1) — the operator's
 * mission-control home and default landing surface. Composes live Sales + SEO
 * signals through the gated data source; anything without a real source yet
 * (composite health, AI summary) renders an honest "calibrating" state.
 * -------------------------------------------------------------------------- */

/* Time-sensitive greeting — uses the viewer's local clock (client-side). */
function timeGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/* Honest qualitative band derived from the composite health score (0..100).
 * The data model carries no trend signal yet, so NO trend arrow is shown - only
 * the band the score actually supports. Thresholds are deliberately
 * conservative: the green "Strong" qualifier requires a genuinely high score,
 * and a low score reads "Needs attention" rather than a fixed green. */
function healthBand(score: number): { label: string; className: string } {
  if (score >= 75) return { label: 'Strong', className: 'text-success' };
  if (score >= 50) return { label: 'Steady', className: 'text-text-secondary' };
  return { label: 'Needs attention', className: 'text-danger' };
}

function VitalCard({ v }: { v: VitalSign }) {
  const engine = engineByKey(v.engineKey);
  const trendColor =
    v.trend === 'up' ? 'text-success' : v.trend === 'down' ? 'text-danger' : 'text-text-tertiary';
  return (
    <div
      data-engine={v.engineKey}
      className="overflow-hidden rounded-md border border-border-subtle bg-surface"
    >
      {/* 3px source-engine accent top edge — accent-as-data, never a fill (§6.2) */}
      <div className="h-accent-edge w-full bg-engine" aria-hidden />
      <div className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-caption text-text-secondary">{v.label}</span>
          {engine && (
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-engine">
              {engine.short}
            </span>
          )}
        </div>
        <div className="mt-2 font-data text-data-lg font-medium text-text-display" data-tabular>
          {v.value}
        </div>
        {v.delta && (
          <div className={cn('mt-1 font-data text-caption font-medium', trendColor)} data-tabular>
            {v.trend === 'up' ? '▲' : v.trend === 'down' ? '▼' : '—'} {v.delta}
          </div>
        )}
        {/* Honest scope: the Executive OS is a WORKSPACE surface — every vital
            here spans all connected domains. Per-domain SEO lives in the SEO
            module, scoped to the active domain. */}
        <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.12em] text-text-tertiary">
          Workspace
        </div>
      </div>
    </div>
  );
}

function AttentionRow({ item, index }: { item: AttentionItem; index: number }) {
  const engine = engineByKey(item.engineKey);
  return (
    <LedgerRow index={index}>
      <Link to={item.href} data-engine={item.engineKey} className="group flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs',
            item.kind === 'risk' ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success',
          )}
          aria-hidden
        >
          {item.kind === 'risk' ? '!' : '↗'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-body-md font-medium text-text-primary group-hover:text-engine">
              {item.title}
            </span>
            {engine && <Badge tone="engine">{engine.short}</Badge>}
          </div>
          <p className="mt-0.5 text-caption text-text-secondary">{item.detail}</p>
        </div>
        <span className="mt-0.5 text-text-tertiary transition-transform group-hover:translate-x-0.5">→</span>
      </Link>
    </LedgerRow>
  );
}

/* Honest-state label helpers for the SEO snapshot — a true zero renders as the
 * number; missing / insufficient-history render an explicit phrase, NEVER 0. */
function organicCountLabel(state: SeoOrganicState, value: number): string {
  if (state === 'ok') return String(value);
  if (state === 'no_domain') return 'No domain connected';
  if (state === 'expired') return 'Refresh needed';
  return 'No data yet';
}
function movementLabel(state: SeoMovementState): string {
  if (state === 'insufficient_history') return 'Not enough ranking history yet';
  if (state === 'no_data') return 'No ranking data yet';
  return ''; // ok / workspace -> caller renders the real number
}
function alertsLabel(state: SeoAlertsState, value: number): string {
  return state === 'not_evaluated' ? 'SEO alerts not evaluated yet' : String(value);
}

/* One figure tile: a short numeric value OR an honest state phrase. */
function StatTile({ label, value, state }: { label: string; value: string; state?: 'number' | 'note' }) {
  return (
    <div className="rounded-md border border-border-subtle bg-surface p-4">
      <div className="h-accent-edge w-full" aria-hidden />
      <div className="text-caption text-text-secondary">{label}</div>
      <div
        className={cn(
          'mt-2 font-data font-medium text-text-display',
          state === 'note' ? 'text-body-md leading-snug text-text-secondary' : 'text-data-lg',
        )}
        data-tabular={state !== 'note' ? '' : undefined}
      >
        {value}
      </div>
    </div>
  );
}

/* Dedicated ranked-organic SEO snapshot — counts, honest gains/losses + alert
 * states, and the top-3 recommended actions, all from the shared ranked-organic
 * selector (cached-only, no paid refresh). Scoped to the active 'Viewing' domain
 * when one is selected (honest per-domain empty); workspace-wide otherwise. */
function SeoSnapshot({ seo, domainHostname }: { seo: SeoExecSnapshot | null; domainHostname: string | null }) {
  if (!seo) {
    return (
      <div className="rounded-md border border-dashed border-border-default bg-surface p-6 text-sm text-text-secondary">
        SEO signal is unavailable right now — figures appear here once the SEO read succeeds.
      </div>
    );
  }
  const movementReal = seo.movementState === 'ok' || seo.movementState === 'workspace';
  return (
    <div data-engine="seo">
      <div className="mb-3">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
          SEO snapshot
        </div>
        <p className="mt-1 text-caption text-text-secondary">
          {domainHostname ? (
            <>Scoped to <span className="font-medium text-text-secondary">{domainHostname}</span> &mdash; the domain selected in Viewing, from the ranked-organic engine (cached).</>
          ) : (
            <>Across your workspace &mdash; spanning all connected domains combined, from the ranked-organic engine (cached). For one domain&rsquo;s keywords and rankings, pick it in the Viewing selector.</>
          )}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <StatTile label="Organic keywords" value={organicCountLabel(seo.organicState, seo.totalOrganicKeywords)} state={seo.organicState === 'ok' ? 'number' : 'note'} />
        <StatTile label="Page-one keywords" value={organicCountLabel(seo.organicState, seo.pageOneKeywords)} state={seo.organicState === 'ok' ? 'number' : 'note'} />
        <StatTile label="Striking distance" value={organicCountLabel(seo.organicState, seo.strikingDistanceKeywords)} state={seo.organicState === 'ok' ? 'number' : 'note'} />
        {movementReal ? (
          <>
            <StatTile label="Ranking gains (30d)" value={String(seo.gains)} state="number" />
            <StatTile label="Ranking losses (30d)" value={String(seo.losses)} state="number" />
          </>
        ) : (
          <div className="col-span-2">
            <StatTile label="Ranking movement (30d)" value={movementLabel(seo.movementState)} state="note" />
          </div>
        )}
        <StatTile label="Open SEO alerts" value={alertsLabel(seo.alertsState, seo.openAlerts)} state={seo.alertsState === 'not_evaluated' ? 'note' : 'number'} />
      </div>

      {/* Top-3 SEO actions — suggested next moves from the shared selector. */}
      <div className="mt-4">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
          Top SEO actions
        </div>
        {seo.topActions.length ? (
          <ol className="mt-2 space-y-2">
            {seo.topActions.slice(0, 3).map((a, i) => (
              <li key={`${a.keyword}-${i}`} className="flex items-start gap-3 rounded-md border border-border-subtle bg-surface p-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-engine/10 font-data text-caption text-engine" aria-hidden>
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-body-md font-medium text-text-primary">
                    {a.keyword || 'Recommended action'}
                    {typeof a.position === 'number' && (
                      <span className="ml-2 font-data text-caption text-text-tertiary">#{a.position}</span>
                    )}
                  </div>
                  {a.action && <p className="mt-0.5 text-caption text-text-secondary">{a.action}</p>}
                </div>
              </li>
            ))}
          </ol>
        ) : seo.organicState === 'ok' ? (
          <p className="mt-2 py-2 text-sm text-text-tertiary">
            No recommended actions right now — the top opportunities are already well placed.
          </p>
        ) : domainHostname ? (
          <p className="mt-2 py-2 text-sm text-text-tertiary">
            No SEO data yet for this domain.
          </p>
        ) : (
          <p className="mt-2 py-2 text-sm text-text-tertiary">
            Recommended actions appear once ranked-organic data is cached for a connected domain.
          </p>
        )}
      </div>
    </div>
  );
}

export function ExecutiveOS() {
  const { user, activeWorkspace } = useAuth();
  // The top-bar 'Viewing' selection is the authoritative domain context for the
  // whole Exec OS (owner 2026-07-21 cross-domain-isolation fix). Thread the
  // active projectId into the composite so the SEO tile scopes to that domain;
  // no domain selected ('All domains' lens) -> honest workspace-wide. projectId
  // is part of the query key, so switching Viewing invalidates + refetches and a
  // previous domain's SEO figures never linger on screen (rule 6).
  const { active } = useActiveDomain();
  const projectId = active?.projectId ?? null;
  const { data: overview, isPending, error, refetch } = useQuery({
    queryKey: ['executive-overview', projectId],
    queryFn: () => fetchExecutiveOverview(projectId),
  });

  if (isPending) {
    return (
      <div className="space-y-8">
        <EyebrowLabel
          as="h1"
          size="lg"
          label={`Executive OS · ${activeWorkspace?.name ?? 'Workspace'}`}
          title={`${timeGreeting()}, ${user?.greeting_name ?? ''}`}
        />
        <LoadingState rows={5} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-8">
        <EyebrowLabel
          as="h1"
          size="lg"
          label={`Executive OS · ${activeWorkspace?.name ?? 'Workspace'}`}
          title={`${timeGreeting()}, ${user?.greeting_name ?? ''}`}
        />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { health, summary, vitals, attention, seo } = overview;
  const risks = attention.filter((a) => a.kind === 'risk');
  const opportunities = attention.filter((a) => a.kind === 'opportunity');

  return (
    <div className="space-y-8">
      <EyebrowLabel
        as="h1"
        size="lg"
        label={`Executive OS · ${activeWorkspace?.name ?? 'Workspace'}`}
        title={`${timeGreeting()}, ${user?.greeting_name ?? ''}`}
      />

      {/* First-run: invite the user to pick a mic mode (hands-free "Hey Aura").
          Renders null once chosen/dismissed or where voice is unavailable. */}
      <AuraMicOnboardingCard />

      {/* Aura — the primary voice entry, front-and-centre on the dashboard home. */}
      <AuraDashboardLauncher greetingName={user?.greeting_name ?? undefined} />

      {/* Hero band — Business Health (63) vs. AI executive summary (37).
          A signature surface: coordinate marks + the locked 63:37 split. */}
      <div className="relative overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-ai">
        <CoordinateMarks />
        <SplitPane
          className="divide-x divide-border-subtle"
          primary={
            <div className="p-6">
              <EyebrowLabel
                size="sm"
                accent="neutral"
                label="Business Health"
                title="Where the workspace stands today"
              />
              {health ? (
                <>
                  <div className="mt-5 flex items-end gap-4">
                    <Score
                      value={health.score}
                      outOf={100}
                      size="lg"
                      label="Business Health"
                      evidence={health.evidence}
                      className="text-[2.75rem] leading-none"
                    />
                    <div className={cn('mb-1 flex items-center gap-1.5', healthBand(health.score).className)}>
                      <span className="text-body-md font-medium">{healthBand(health.score).label}</span>
                    </div>
                  </div>
                  <TickRule className="mt-6" />
                  <p className="mt-4 max-w-prose text-body-md text-text-secondary">
                    A weighted composite of live Sales and SEO signals. Every input is
                    source-attributed — tap the score to open the evidence.
                  </p>
                </>
              ) : (
                <>
                  <div className="mt-5 font-display text-xl font-semibold text-text-primary">
                    Calibrating — not enough signal yet
                  </div>
                  <TickRule className="mt-6" />
                  <p className="mt-4 max-w-prose text-body-md text-text-secondary">
                    Business Health is a workspace-wide weighted composite of live Sales and SEO
                    signals across all connected domains. It appears once your engines have produced
                    their first data — it is never an invented number.
                  </p>
                </>
              )}
            </div>
          }
          secondary={
            <div data-engine="ai" className="h-full bg-ai-wash/50 p-6">
              <div className="flex items-center gap-2">
                <span aria-hidden className="text-ai">✧</span>
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
                  AI Executive Summary
                </span>
              </div>
              {summary ? (
                <InsightText
                  text={summary}
                  className="mt-3 font-display text-[1.0625rem] leading-relaxed text-text-primary"
                />
              ) : (
                <p className="mt-3 text-body-md text-text-secondary">
                  No summary yet — the copilot writes a workspace-wide summary once there is live
                  engine activity to summarise. It is advisory, never an invented narrative.
                </p>
              )}
              <p className="mt-4 border-t border-ai/20 pt-3 text-caption text-text-tertiary">
                Advisory · authored by the copilot from Sales + SEO signals. You decide the next move.
              </p>
            </div>
          }
        />
      </div>

      {/* 4-up vital signs (§7.1) */}
      <div>
        <div className="mb-3">
          <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-text-tertiary">
            Vital signs
          </div>
          <p className="mt-1 text-caption text-text-secondary">
            Across your workspace — every figure below spans all connected domains combined.
            For a single domain&rsquo;s keywords, rankings and competitors, open the SEO engine and pick a domain.
          </p>
        </div>
        {vitals.length ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {vitals.map((v) => (
              <VitalCard key={v.key} v={v} />
            ))}
          </div>
        ) : (
          <div className="rounded-md border border-dashed border-border-default bg-surface p-6 text-sm text-text-secondary">
            No vital signs yet — metrics appear here as your engines start producing data.
          </div>
        )}
      </div>

      {/* Ranked-organic SEO snapshot — scoped to the active 'Viewing' domain
          (honest per-domain empty) or workspace-wide under the 'All domains' lens. */}
      <SeoSnapshot seo={seo} domainHostname={active?.hostname ?? null} />

      {/* Risks + opportunities — ledger rows, not cards (§2.7.2) */}
      <div className="grid gap-8 md:grid-cols-2">
        <section>
          <h2 className="mb-1 flex items-center gap-2 text-heading-md font-semibold text-text-primary">
            <span className="text-danger" aria-hidden>!</span> Risks
          </h2>
          <div>
            {risks.length ? (
              risks.map((r, i) => <AttentionRow key={r.id} item={r} index={i + 1} />)
            ) : (
              <p className="py-3 text-sm text-text-tertiary">
                Nothing needs your attention right now.
              </p>
            )}
          </div>
        </section>
        <section>
          <h2 className="mb-1 flex items-center gap-2 text-heading-md font-semibold text-text-primary">
            <span className="text-success" aria-hidden>↗</span> Opportunities
          </h2>
          <div>
            {opportunities.length ? (
              opportunities.map((o, i) => <AttentionRow key={o.id} item={o} index={i + 1} />)
            ) : (
              <p className="py-3 text-sm text-text-tertiary">
                No opportunities flagged yet — they surface here as engines find them.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
